// One accident or incident in full (Session AOM). Office/admin. The three parts of the paper form,
// each editable in place, the forward-only status workflow as BUTTONS (never a free dropdown), the
// generated document, and the delete control.
//
// THE RIDDOR PANEL IS THE ONE WORTH READING CAREFULLY. It shows the eight categories with HSE's own
// criteria beside each, fetched from the server so the text the office reads and the text
// snapshotted onto the record are one body of words. Picking a category does three things at once,
// all server-side: it records the determination, DERIVES the reportable boolean from it (the office
// never types that), and snapshots the timescale guidance as it stood at that moment. Every one of
// them can be cleared again, because a determination made in error must not stand for ever on a
// statutory record.
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  api,
  ApiError,
  downloadAccidentReportPdf,
  type AccidentDetail,
  type AccidentEventType,
  type AccidentHospitalAdmission,
  type AccidentRiddorCategory,
  type AccidentStatus,
  type RiddorReference,
} from "../../lib/api";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { useOptionsLoader } from "../../lib/useOptionsLoader";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { RecordHeader } from "../../components/RecordHeader";
import { StatusPill } from "../../components/StatusPill";
import { statusEntry } from "../../lib/statusRegistry";
import { formatDate, formatDateTime } from "../../lib/format";
import {
  EVENT_TYPES,
  EVENT_TYPE_LABEL,
  HOSPITAL_LABEL,
  HOSPITAL_OPTIONS,
  RIDDOR_CATEGORY_ORDER,
  STATUS_LABEL,
  riddorKey,
  toDateInputValue,
  toLocalInputValue,
  writtenReportCountdown,
} from "./accidentsUi";

const inputCls =
  "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green disabled:opacity-50";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";
const sectionCls = "mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-5";

export function AccidentDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [record, setRecord] = useState<AccidentDetail | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "notfound" | "error">("loading");

  const load = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!opts?.silent) setState("loading");
      try {
        setRecord(await api.accidents.get(id));
        setState("ready");
      } catch (err) {
        if (!opts?.silent) setState(err instanceof ApiError && err.status === 404 ? "notfound" : "error");
      }
    },
    [id],
  );
  const refresh = useCallback(() => load({ silent: true }), [load]);
  useEffect(() => {
    void load();
  }, [load]);

  if (state === "loading") return <Loader shape="panel" className="p-6" />;
  if (state === "notfound") return <p className="p-6 text-center text-ele-body">That record could not be found.</p>;
  if (state === "error" || !record)
    return (
      <p role="alert" className="p-6 text-center text-ele-strong">
        Could not load this record. Please try again.
      </p>
    );

  const countdown = writtenReportCountdown(record.daysToWrittenReport);

  return (
    <div className="mx-auto max-w-4xl px-4 py-6">
      <RecordHeader
        className="mb-4"
        title={record.ref}
        backTo="/office/accidents"
        backLabel="Back to the register"
        status={
          <>
            <StatusPill word={STATUS_LABEL[record.status] ?? record.status} tone={statusEntry("accident", record.status).tone} />
            <StatusPill domain="riddor" status={riddorKey(record.riddorReportable)} />
            <span className="text-sm text-ele-body">{formatDateTime(record.dateOfEvent)}</span>
            {record.archivedAt && <StatusPill word="Archived" tone="grey" variant="outline" />}
          </>
        }
      />

      {countdown && (
        <div role="alert" className={`mb-4 rounded-ele border p-3 text-sm ${countdown.urgent ? "border-ele-danger bg-red-500/10" : "border-ele-purple-accent bg-ele-surface"}`}>
          <p className={`font-heading uppercase tracking-wide ${countdown.urgent ? "text-ele-danger" : "text-ele-blue"}`}>{countdown.text}</p>
          {record.riddorTimescaleGuidance && <p className="mt-1 text-ele-body">{record.riddorTimescaleGuidance}</p>}
        </div>
      )}

      {record.archivedAt && <RestorePanel record={record} onChanged={refresh} />}

      <StatusPanel record={record} onChanged={refresh} />
      <ReportPanel record={record} onSaved={refresh} />
      <RiddorPanel record={record} onSaved={refresh} />
      <InvestigationPanel record={record} onSaved={refresh} />
      <DocumentPanel record={record} />
      <DeleteSection record={record} onDeleted={() => navigate("/office/accidents")} />
    </div>
  );
}

function RestorePanel({ record, onChanged }: { record: AccidentDetail; onChanged: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <section className="mb-4 rounded-ele border border-ele-hairline bg-ele-surface-soft p-4">
      <p className="text-sm text-ele-body">
        This record is archived and is hidden from the register by default. It is kept in full: nothing has been deleted.
      </p>
      <button
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          setError(null);
          try {
            await api.accidents.restore(record.id);
            await onChanged();
          } catch (err) {
            setError(err instanceof ApiError ? err.message || err.code : "Could not restore this record.");
          } finally {
            setBusy(false);
          }
        }}
        className="mt-2 rounded-ele border border-ele-purple-accent px-4 py-1.5 font-menu text-xs tracking-wide text-ele-body hover:border-ele-green hover:text-ele-green disabled:opacity-40"
      >
        {busy ? "Restoring..." : "Restore to the register"}
      </button>
      {error && (
        <p role="alert" className="mt-2 text-sm text-ele-danger">
          {error}
        </p>
      )}
    </section>
  );
}

// Forward-only, as BUTTONS rather than a dropdown, matching the internal-audit pattern: the office
// moves a record on, it never sets a status arbitrarily. Both server preconditions are mirrored here
// so a blocked control explains ITSELF instead of the office pressing it and reading a refusal.
function StatusPanel({ record, onChanged }: { record: AccidentDetail; onChanged: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function move(status: AccidentStatus) {
    setBusy(true);
    setError(null);
    try {
      await api.accidents.update(record.id, { status });
      await onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message || err.code : "Could not update the status.");
    } finally {
      setBusy(false);
    }
  }

  function blockedReason(s: AccidentStatus): string | null {
    if (s === "reported_to_hse" && !record.canMarkReportedToHse) return "Record the date the report was made to the HSE first.";
    if (s === "closed" && !record.canClose) return "Record the RIDDOR determination first, even if it is not reportable.";
    return null;
  }

  return (
    <section className={sectionCls}>
      <h2 className="mb-2 font-heading text-lg text-ele-strong">Status</h2>
      {record.allowedNextStatuses.length === 0 ? (
        <p className="text-sm text-ele-body">This record is closed. No further status changes are possible.</p>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-ele-body">Move forward to:</span>
          {record.allowedNextStatuses.map((s) => {
            const blocked = blockedReason(s);
            return (
              <button
                key={s}
                disabled={busy || Boolean(blocked)}
                title={blocked ?? undefined}
                onClick={() => move(s)}
                className="rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green disabled:opacity-40"
              >
                {STATUS_LABEL[s]}
              </button>
            );
          })}
        </div>
      )}
      {/* A disabled button's title never paints on touch and is easy to miss with a mouse, so the
          reason is also stated in the open. */}
      {record.allowedNextStatuses.map((s) => {
        const blocked = blockedReason(s);
        return blocked ? (
          <p key={s} className="mt-2 text-xs text-ele-body">
            {STATUS_LABEL[s]}: {blocked}
          </p>
        ) : null;
      })}
      {record.closedAt && (
        <p className="mt-2 text-xs text-ele-body">
          Closed {formatDate(record.closedAt)}
          {record.closedByName ? ` by ${record.closedByName}` : ""}.
        </p>
      )}
      {error && (
        <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-red-500/10 px-3 py-2 text-sm text-ele-danger">
          {error}
        </p>
      )}
    </section>
  );
}

// Part 1 and 2 of the paper form: what happened, and who was hurt.
function ReportPanel({ record, onSaved }: { record: AccidentDetail; onSaved: () => Promise<void> }) {
  const [form, setForm] = useState(() => seedReport(record));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setForm(seedReport(record));
  }, [record]);

  const set =
    (name: keyof ReturnType<typeof seedReport>) =>
    (e: { target: { value: string } }) => {
      setForm((f) => ({ ...f, [name]: e.target.value }));
      setSaved(false);
    };

  async function save(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.accidents.update(record.id, {
        dateOfEvent: form.dateOfEvent || null,
        eventType: form.eventType as AccidentEventType,
        location: form.location.trim() || null,
        description: form.description.trim() || undefined,
        immediateAction: form.immediateAction.trim() || null,
        witnesses: form.witnesses.trim() || null,
        injuredName: form.injuredName.trim() || null,
        injuredRole: form.injuredRole.trim() || null,
        injuredEmployer: form.injuredEmployer.trim() || null,
        injuredContact: form.injuredContact.trim() || null,
        natureOfInjury: form.natureOfInjury.trim() || null,
        bodyPartAffected: form.bodyPartAffected.trim() || null,
        hospitalAdmission: (form.hospitalAdmission || null) as AccidentHospitalAdmission | null,
      });
      setSaved(true);
      await onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message || err.code : "Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className={sectionCls}>
      <h2 className="mb-1 font-heading text-lg text-ele-strong">Part 1. What happened</h2>
      <p className="mb-3 text-xs text-ele-body">
        {record.siteName ? `At ${record.siteName}` : "No site on the register"}
        {record.eleNumber ? ` · ${record.eleNumber}` : ""}
        {record.reportedByName ? ` · Reported by ${record.reportedByName}` : ""}
      </p>
      <form onSubmit={save} className="grid gap-3 sm:grid-cols-2">
        <label>
          <span className={labelCls}>When did it happen</span>
          <input aria-label="When did it happen" type="datetime-local" value={form.dateOfEvent} onChange={set("dateOfEvent")} className={inputCls} />
        </label>
        <label>
          <span className={labelCls}>Type of event</span>
          <select aria-label="Type of event" value={form.eventType} onChange={set("eventType")} className={inputCls}>
            {EVENT_TYPES.map((t) => (
              <option key={t} value={t}>
                {EVENT_TYPE_LABEL[t]}
              </option>
            ))}
          </select>
        </label>
        <label className="sm:col-span-2">
          <span className={labelCls}>Where exactly</span>
          <input aria-label="Where exactly" value={form.location} onChange={set("location")} className={inputCls} />
        </label>
        <label className="sm:col-span-2">
          <span className={labelCls}>What happened</span>
          <textarea aria-label="What happened" value={form.description} onChange={set("description")} rows={4} className={inputCls} />
        </label>
        <label className="sm:col-span-2">
          <span className={labelCls}>Immediate action taken</span>
          <textarea aria-label="Immediate action taken" value={form.immediateAction} onChange={set("immediateAction")} rows={2} className={inputCls} />
        </label>
        <label className="sm:col-span-2">
          <span className={labelCls}>Witnesses</span>
          <input aria-label="Witnesses" value={form.witnesses} onChange={set("witnesses")} className={inputCls} />
        </label>

        <h3 className="sm:col-span-2 mt-2 font-heading text-base text-ele-strong">Part 2. Injured person</h3>
        <label>
          <span className={labelCls}>Name</span>
          <input aria-label="Injured person name" value={form.injuredName} onChange={set("injuredName")} className={inputCls} />
        </label>
        <label>
          <span className={labelCls}>Role or occupation</span>
          <input aria-label="Injured person role" value={form.injuredRole} onChange={set("injuredRole")} className={inputCls} />
        </label>
        <label>
          <span className={labelCls}>Employer</span>
          <input aria-label="Injured person employer" value={form.injuredEmployer} onChange={set("injuredEmployer")} className={inputCls} />
        </label>
        <label>
          <span className={labelCls}>Contact</span>
          <input aria-label="Injured person contact" value={form.injuredContact} onChange={set("injuredContact")} className={inputCls} />
        </label>
        <label>
          <span className={labelCls}>Nature of the injury</span>
          <input aria-label="Nature of the injury" value={form.natureOfInjury} onChange={set("natureOfInjury")} className={inputCls} />
        </label>
        <label>
          <span className={labelCls}>Part of the body affected</span>
          <input aria-label="Part of the body affected" value={form.bodyPartAffected} onChange={set("bodyPartAffected")} className={inputCls} />
        </label>
        <label className="sm:col-span-2">
          <span className={labelCls}>Hospital</span>
          <select aria-label="Hospital" value={form.hospitalAdmission} onChange={set("hospitalAdmission")} className={inputCls}>
            <option value="">Not recorded</option>
            {HOSPITAL_OPTIONS.map((h) => (
              <option key={h} value={h}>
                {HOSPITAL_LABEL[h]}
              </option>
            ))}
          </select>
        </label>

        <div className="sm:col-span-2 flex items-center gap-3">
          <Button type="submit" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
          {saved && (
            <span role="status" className="text-sm text-ele-green">
              Saved.
            </span>
          )}
        </div>
      </form>
      {error && (
        <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-red-500/10 px-3 py-2 text-sm text-ele-danger">
          {error}
        </p>
      )}
    </section>
  );
}

// Part 3: the RIDDOR assessment. The reference table is FETCHED, never mirrored here, so the criteria
// on screen and the guidance stored on the record are the same words.
function RiddorPanel({ record, onSaved }: { record: AccidentDetail; onSaved: () => Promise<void> }) {
  const refState = useOptionsLoader<RiddorReference>(() => api.accidents.riddorReference(), []);
  const reference = refState.data;
  const [category, setCategory] = useState<string>(record.riddorCategory ?? "");
  const [form, setForm] = useState(() => seedRiddor(record));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setCategory(record.riddorCategory ?? "");
    setForm(seedRiddor(record));
  }, [record]);

  const byKey = new Map((reference?.categories ?? []).map((c) => [c.key, c]));
  const chosen = category ? byKey.get(category as AccidentRiddorCategory) : undefined;
  const ordered = RIDDOR_CATEGORY_ORDER.map((k) => byKey.get(k)).filter((c): c is NonNullable<typeof c> => Boolean(c));

  async function save(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.accidents.update(record.id, {
        // Null clears the determination entirely: the reportable boolean and the guidance snapshot
        // go with it, server-side. A determination made in error must be genuinely reversible.
        riddorCategory: (category || null) as AccidentRiddorCategory | null,
        hseReportedAt: form.hseReportedAt || null,
        hseReportMethod: (form.hseReportMethod || null) as "online" | "telephone" | null,
        hseReferenceNumber: form.hseReferenceNumber.trim() || null,
      });
      setSaved(true);
      await onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message || err.code : "Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className={sectionCls}>
      <h2 className="mb-1 font-heading text-lg text-ele-strong">Part 3. RIDDOR assessment</h2>
      <p className="mb-3 text-xs text-ele-body opacity-80">
        {reference?.disclaimer ?? "This is guidance, not legal advice. Refer to HSE guidance at hse.gov.uk/riddor."}
      </p>
      {refState.error && (
        <p className="mb-3 text-xs text-ele-danger">
          Could not load the RIDDOR criteria.{" "}
          <button type="button" onClick={refState.retry} className="underline hover:text-ele-strong">
            Retry
          </button>
        </p>
      )}

      <form onSubmit={save} className="grid gap-3 sm:grid-cols-2">
        <label className="sm:col-span-2">
          <span className={labelCls}>Is this reportable under RIDDOR 2013</span>
          <select
            aria-label="RIDDOR category"
            value={category}
            onChange={(e) => {
              setCategory(e.target.value);
              setSaved(false);
            }}
            className={inputCls}
          >
            <option value="">Not yet assessed</option>
            {ordered.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </select>
        </label>

        {chosen && (
          <div className="sm:col-span-2 rounded-ele border border-ele-purple-accent bg-ele-inset p-3 text-sm">
            <p className="font-menu text-xs uppercase tracking-wide text-ele-blue">
              {chosen.reportable ? "Reportable" : "Not reportable"}
              {chosen.form ? ` · ${chosen.form}` : ""}
            </p>
            <p className="mt-1 text-ele-body">{chosen.criteria}</p>
            <p className="mt-2 text-ele-body">{chosen.timescaleGuidance}</p>
          </div>
        )}

        <label>
          <span className={labelCls}>Reported to the HSE on</span>
          <input
            aria-label="Reported to the HSE on"
            type="date"
            value={form.hseReportedAt}
            onChange={(e) => {
              setForm((f) => ({ ...f, hseReportedAt: e.target.value }));
              setSaved(false);
            }}
            className={inputCls}
          />
        </label>
        <label>
          <span className={labelCls}>How it was reported</span>
          <select
            aria-label="How it was reported"
            value={form.hseReportMethod}
            onChange={(e) => {
              setForm((f) => ({ ...f, hseReportMethod: e.target.value }));
              setSaved(false);
            }}
            className={inputCls}
          >
            <option value="">Not recorded</option>
            <option value="online">Online</option>
            <option value="telephone">Telephone</option>
          </select>
        </label>
        <label className="sm:col-span-2">
          <span className={labelCls}>HSE reference number</span>
          <input
            aria-label="HSE reference number"
            value={form.hseReferenceNumber}
            onChange={(e) => {
              setForm((f) => ({ ...f, hseReferenceNumber: e.target.value }));
              setSaved(false);
            }}
            className={inputCls}
          />
        </label>

        {record.riddorAssessedAt && (
          <p className="sm:col-span-2 text-xs text-ele-body">
            Assessed {formatDate(record.riddorAssessedAt)}
            {record.riddorAssessedByName ? ` by ${record.riddorAssessedByName}` : ""}. The assessment can be changed or cleared at any time.
          </p>
        )}

        <div className="sm:col-span-2 flex items-center gap-3">
          <Button type="submit" disabled={saving}>
            {saving ? "Saving..." : "Save the assessment"}
          </Button>
          {saved && (
            <span role="status" className="text-sm text-ele-green">
              Saved.
            </span>
          )}
        </div>
      </form>
      {error && (
        <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-red-500/10 px-3 py-2 text-sm text-ele-danger">
          {error}
        </p>
      )}
    </section>
  );
}

// Part 4: the investigation. Field names deliberately mirror the Complaint register's own
// rootCause / correctiveAction idiom rather than inventing new terms for the same ideas.
function InvestigationPanel({ record, onSaved }: { record: AccidentDetail; onSaved: () => Promise<void> }) {
  const refState = useOptionsLoader<RiddorReference>(() => api.accidents.riddorReference(), []);
  const suggestions = refState.data?.evidenceSuggestions ?? [];
  const [form, setForm] = useState(() => seedInvestigation(record));
  const [evidence, setEvidence] = useState<string[]>(record.evidenceAttached);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setForm(seedInvestigation(record));
    setEvidence(record.evidenceAttached);
  }, [record]);

  const set =
    (name: keyof ReturnType<typeof seedInvestigation>) =>
    (e: { target: { value: string } }) => {
      setForm((f) => ({ ...f, [name]: e.target.value }));
      setSaved(false);
    };

  function toggleEvidence(tag: string) {
    setEvidence((e) => (e.includes(tag) ? e.filter((x) => x !== tag) : [...e, tag]));
    setSaved(false);
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.accidents.update(record.id, {
        investigatorName: form.investigatorName.trim() || null,
        investigationStartedAt: form.investigationStartedAt || null,
        investigationCompletedAt: form.investigationCompletedAt || null,
        sequenceOfEvents: form.sequenceOfEvents.trim() || null,
        rootCause: form.rootCause.trim() || null,
        contributingFactors: form.contributingFactors.trim() || null,
        immediateActionToPreventRecurrence: form.immediateActionToPreventRecurrence.trim() || null,
        correctiveAction: form.correctiveAction.trim() || null,
        evidenceAttached: evidence,
      });
      setSaved(true);
      await onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message || err.code : "Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className={sectionCls}>
      <h2 className="mb-3 font-heading text-lg text-ele-strong">Part 4. Investigation</h2>
      <form onSubmit={save} className="grid gap-3 sm:grid-cols-3">
        <label>
          <span className={labelCls}>Investigated by</span>
          <input aria-label="Investigated by" value={form.investigatorName} onChange={set("investigatorName")} className={inputCls} />
        </label>
        <label>
          <span className={labelCls}>Started</span>
          <input aria-label="Investigation started" type="date" value={form.investigationStartedAt} onChange={set("investigationStartedAt")} className={inputCls} />
        </label>
        <label>
          <span className={labelCls}>Completed</span>
          <input
            aria-label="Investigation completed"
            type="date"
            value={form.investigationCompletedAt}
            onChange={set("investigationCompletedAt")}
            className={inputCls}
          />
        </label>
        <label className="sm:col-span-3">
          <span className={labelCls}>Sequence of events</span>
          <textarea aria-label="Sequence of events" value={form.sequenceOfEvents} onChange={set("sequenceOfEvents")} rows={3} className={inputCls} />
        </label>
        <label className="sm:col-span-3">
          <span className={labelCls}>Root cause</span>
          <textarea aria-label="Root cause" value={form.rootCause} onChange={set("rootCause")} rows={2} className={inputCls} />
        </label>
        <label className="sm:col-span-3">
          <span className={labelCls}>Contributing factors</span>
          <textarea aria-label="Contributing factors" value={form.contributingFactors} onChange={set("contributingFactors")} rows={2} className={inputCls} />
        </label>
        <label className="sm:col-span-3">
          <span className={labelCls}>Immediate action to prevent recurrence</span>
          <textarea
            aria-label="Immediate action to prevent recurrence"
            value={form.immediateActionToPreventRecurrence}
            onChange={set("immediateActionToPreventRecurrence")}
            rows={2}
            className={inputCls}
          />
        </label>
        <label className="sm:col-span-3">
          <span className={labelCls}>Corrective action</span>
          <textarea aria-label="Corrective action" value={form.correctiveAction} onChange={set("correctiveAction")} rows={2} className={inputCls} />
        </label>

        {/* Suggestions, not a closed list: the server stores free text, because a real lift
            investigation attaches things no fixed list written today would contain. */}
        <fieldset className="sm:col-span-3">
          <legend className={labelCls}>Evidence attached</legend>
          <div className="flex flex-wrap gap-2">
            {[...new Set([...suggestions, ...evidence])].map((tag) => (
              <button
                type="button"
                key={tag}
                onClick={() => toggleEvidence(tag)}
                className={`rounded-ele border px-3 py-1.5 font-menu text-xs tracking-wide transition ${
                  evidence.includes(tag) ? "border-ele-green bg-ele-inset text-ele-green" : "border-ele-purple-accent text-ele-body hover:text-ele-green"
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        </fieldset>

        <div className="sm:col-span-3 flex items-center gap-3">
          <Button type="submit" disabled={saving}>
            {saving ? "Saving..." : "Save the investigation"}
          </Button>
          {saved && (
            <span role="status" className="text-sm text-ele-green">
              Saved.
            </span>
          )}
        </div>
      </form>
      {error && (
        <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-red-500/10 px-3 py-2 text-sm text-ele-danger">
          {error}
        </p>
      )}
    </section>
  );
}

function DocumentPanel({ record }: { record: AccidentDetail }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <section className={sectionCls}>
      <h2 className="mb-2 font-heading text-lg text-ele-strong">Accident report document</h2>
      <p className="mb-3 text-sm text-ele-body">
        The branded accident report and investigation document, carrying all four parts. Every field prints whether or not it is filled in, so
        this is also the blank form to hand an assessor who asks for one.
      </p>
      <button
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          setError(null);
          try {
            await downloadAccidentReportPdf(record.id, record.ref);
          } catch (err) {
            setError(err instanceof ApiError ? err.message || err.code : "Could not generate the document.");
          } finally {
            setBusy(false);
          }
        }}
        className="rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:border-ele-green hover:text-ele-green disabled:opacity-40"
      >
        {busy ? "Generating..." : "Download document"}
      </button>
      {error && (
        <p role="alert" className="mt-2 text-sm text-ele-danger">
          {error}
        </p>
      )}
    </section>
  );
}

function DeleteSection({ record, onDeleted }: { record: AccidentDetail; onDeleted: () => void }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Mirror the server rule exactly: a record nothing has been done to hard-deletes; anything with a
  // determination, an HSE report or investigation content is archived and kept.
  const willArchive =
    record.status !== "open" ||
    Boolean(record.riddorCategory) ||
    Boolean(record.hseReportedAt) ||
    Boolean(record.investigationStartedAt) ||
    Boolean(record.rootCause && record.rootCause.trim()) ||
    Boolean(record.correctiveAction && record.correctiveAction.trim());

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      await api.accidents.remove(record.id);
      onDeleted();
    } catch (err) {
      setError(err instanceof ApiError ? err.message || err.code : "Could not delete this record.");
      setBusy(false);
    }
  }

  return (
    <section className="rounded-ele border border-ele-hairline bg-ele-surface-soft p-5">
      <Button
        variant="destructive"
        size="sm"
        onClick={() => {
          setError(null);
          setOpen(true);
        }}
      >
        {willArchive ? "Archive record" : "Delete record"}
      </Button>
      <ConfirmDeleteModal
        open={open}
        variant={willArchive ? "archive" : "destructive"}
        title={willArchive ? "Archive this record?" : "Delete this record?"}
        message={
          willArchive
            ? "This record carries a RIDDOR determination, an HSE report or investigation history, so it will be archived and kept in full for the statutory record, not deleted. It can be restored."
            : "Nothing has been recorded against this beyond the initial report, so it will be permanently deleted. This cannot be undone."
        }
        confirmLabel={willArchive ? "Archive record" : "Delete record"}
        busy={busy}
        error={error}
        onConfirm={confirm}
        onCancel={() => {
          if (!busy) {
            setOpen(false);
            setError(null);
          }
        }}
      />
    </section>
  );
}

function seedReport(r: AccidentDetail) {
  return {
    dateOfEvent: toLocalInputValue(r.dateOfEvent),
    eventType: r.eventType as string,
    location: r.location ?? "",
    description: r.description,
    immediateAction: r.immediateAction ?? "",
    witnesses: r.witnesses ?? "",
    injuredName: r.injuredName ?? "",
    injuredRole: r.injuredRole ?? "",
    injuredEmployer: r.injuredEmployer ?? "",
    injuredContact: r.injuredContact ?? "",
    natureOfInjury: r.natureOfInjury ?? "",
    bodyPartAffected: r.bodyPartAffected ?? "",
    hospitalAdmission: (r.hospitalAdmission ?? "") as string,
  };
}

function seedRiddor(r: AccidentDetail) {
  return {
    hseReportedAt: toDateInputValue(r.hseReportedAt),
    hseReportMethod: r.hseReportMethod ?? "",
    hseReferenceNumber: r.hseReferenceNumber ?? "",
  };
}

function seedInvestigation(r: AccidentDetail) {
  return {
    investigatorName: r.investigatorName ?? "",
    investigationStartedAt: toDateInputValue(r.investigationStartedAt),
    investigationCompletedAt: toDateInputValue(r.investigationCompletedAt),
    sequenceOfEvents: r.sequenceOfEvents ?? "",
    rootCause: r.rootCause ?? "",
    contributingFactors: r.contributingFactors ?? "",
    immediateActionToPreventRecurrence: r.immediateActionToPreventRecurrence ?? "",
    correctiveAction: r.correctiveAction ?? "",
  };
}
